import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Gift } from 'lucide-react';
import axios from 'axios';
import LightLotoCluster from './LightLotoCluster';
import LightLotoProCluster from './LightLotoProCluster';

const API_BASE = 'https://api.light-platform.com/api';

/**
 * Общая обёртка для трёх лотерей.
 *
 * Лотерея 1$ рендерится СТАРЫМ компонентом LightLotoCluster — он не меняется
 * ни строкой и продолжает работать на своих эндпоинтах /api/lottery/*.
 * Номиналы 2$ и 4$ рендерятся новым LightLotoProCluster.
 *
 * Обёртка добавляет только переключатель номинала и кнопку «назад».
 *
 *   <LightLotoHub onBack={...} />              // откроется на лотерее 1$
 *   <LightLotoHub initialTier={2} onBack={} /> // сразу на лотерее 2$
 */

const TABS = [
    {
        tier: 1,
        label: 'Лотерея 1$',
        price: 1,
        infoPath: '/lottery/info',
        active: 'bg-gradient-to-r from-[#2b6c8e] to-[#a7d7cd] text-white',
        idle: 'text-[#2b6c8e] hover:bg-[#d5ebe6]',
    },
    {
        tier: 2,
        label: 'Лотерея 2$',
        price: 2,
        infoPath: '/lottery/2/info',
        active: 'bg-gradient-to-r from-violet-500 to-indigo-600 text-white',
        idle: 'text-violet-600 hover:bg-violet-50',
    },
    {
        tier: 4,
        label: 'Лотерея 4$',
        price: 4,
        infoPath: '/lottery/4/info',
        active: 'bg-gradient-to-r from-rose-500 to-orange-600 text-white',
        idle: 'text-rose-600 hover:bg-rose-50',
    },
];

const LightLotoHub = ({ initialTier = 1, onBack }) => {
    const [tier, setTier] = useState(initialTier);
    const [jackpots, setJackpots] = useState({});

    // Джекпоты для подписей на вкладках. Ошибки глушим:
    // это украшение, оно не должно ронять страницу.
    const loadJackpots = useCallback(async () => {
        const headers = {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        };

        const results = await Promise.allSettled(
            TABS.map((t) => axios.get(`${API_BASE}${t.infoPath}`, { headers }))
        );

        const next = {};
        results.forEach((res, i) => {
            if (res.status === 'fulfilled' && res.value?.data?.success) {
                next[TABS[i].tier] = res.value.data.data.jackpot_amount;
            }
        });
        setJackpots(next);
    }, []);

    useEffect(() => {
        loadJackpots();
    }, [loadJackpots]);

    return (
        <div>
            {/* ---------- Переключатель номинала ---------- */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
                <div className="max-w-6xl mx-auto px-4 py-3">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors mb-3"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="text-sm font-medium">К кластерам</span>
                        </button>
                    )}

                    <div className="flex items-center gap-2 overflow-x-auto pb-1">
                        <Gift className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        {TABS.map((t) => {
                            const isActive = tier === t.tier;
                            return (
                                <button
                                    key={t.tier}
                                    onClick={() => setTier(t.tier)}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`px-4 py-2 rounded-xl font-semibold text-sm whitespace-nowrap transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-gray-400 ${
                                        isActive ? `${t.active} shadow-md` : t.idle
                                    }`}
                                >
                                    <span className="block">{t.label}</span>
                                    {jackpots[t.tier] != null && (
                                        <span className={`block text-[10px] font-normal ${isActive ? 'opacity-90' : 'opacity-60'}`}>
                                            джекпот {Number(jackpots[t.tier]).toFixed(0)}$
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ---------- Сама лотерея ---------- */}
            {/*
                key={tier} — при смене номинала компонент пересоздаётся начисто,
                чтобы состояние одной лотереи не утекло в другую.
                Незавершённый билет при этом не теряется: он лежит на сервере
                и возвращается через «Историю билетов».
            */}
            {tier === 1
                ? <LightLotoCluster key="tier-1" />
                : <LightLotoProCluster key={`tier-${tier}`} tier={tier} />}
        </div>
    );
};

export default LightLotoHub;
